import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import { PointAction, PointActionType } from 'src/features/points/types';
import axios from 'src/lib/axios';
import { type MutationConfig } from 'src/lib/react-query';
import { formatResourcePath } from 'src/utils/regex';

import type { Point } from 'src/features/points/types';
import type { GeneralSmartParkError } from 'src/types';

const RESOURCE_OPEN = '/parkings/:parkingId/points/:pointId/open/';
const RESOURCE_CLOSE = '/parkings/:parkingId/points/:pointId/close/';

type FormSchemaValues = z.infer<typeof formSchema>;
const formSchema = z
    .object({
        pointId: z.string().trim().min(1),
        reason: z.string().optional(),
        action_type: z.nativeEnum(PointActionType),
    })
    // We require a reason for a normal action, otherwise its an emergency action
    .refine(({ action_type, reason }) => {
        return action_type === PointActionType.NORMAL ? Boolean(reason) : true;
    });

interface DTO extends FormSchemaValues {
    parkingId: string;
    action: PointAction;
}

type MutationFnType = typeof mutationFn;
const mutationFn = ({ parkingId, pointId, action, ...data }: DTO): Promise<Point> => {
    return axios.post(
        formatResourcePath(action == PointAction.OPEN_BARRIER ? RESOURCE_OPEN : RESOURCE_CLOSE, {
            parkingId,
            pointId,
        }),
        data,
    );
};

interface UseMutationHookOptions extends MutationConfig<MutationFnType, GeneralSmartParkError> {}
const useMutationHook = (options: UseMutationHookOptions = {}) =>
    useMutation({
        mutationFn: mutationFn,
        ...options,
    });

export { useMutationHook as usePointAction, formSchema as pointActionFormSchema };
export type { DTO as PointActionDTO, FormSchemaValues as PointActionFormSchemaValues };
